import { useState, useEffect, useMemo } from 'react';
import { useAuthStore } from '../store/auth';
import { useNavigate, useLocation } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { User } from '@lms/shared/types';
import { Button, Input } from '@lms/ui';
import { apiClient, updateApiClientToken } from '../lib/api-client';
import { getNeonAuthClient, getNeonJwtToken } from '../lib/neon-auth';
import { ArrowLeft, ArrowRight, BookOpenCheck, Send } from 'lucide-react';
import { useLanguage } from '../lib/LanguageContext';
import { copy } from '../copy/copy';
import { Bilingual } from '../components/Bilingual';

interface ParticipantDestination {
  pathname: string;
  search?: string;
  hash?: string;
}

interface LoginNavigationState {
  message?: { type: 'success' | 'error'; text: string };
  from?: ParticipantDestination;
}

interface LoginFormInput {
  identifier: string;
  password?: string;
}

function isRateLimitedError(error: unknown): boolean {
  return (
    typeof error === 'object' && error !== null && 'statusCode' in error && error.statusCode === 429
  );
}

function getSafeDestination(state: LoginNavigationState | null): ParticipantDestination {
  const destination = state?.from;
  if (!destination?.pathname.startsWith('/')) return { pathname: '/' };
  return destination;
}

export function Login() {
  const navigate = useNavigate();
  const setAuth = useAuthStore(state => state.setAuth);
  const [isForgotMode, setIsForgotMode] = useState(false);
  const location = useLocation();
  const navigationState = location.state as LoginNavigationState | null;
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(
    navigationState?.message || null
  );
  const { language, setLanguage, t } = useLanguage();
  const authSchema = useMemo(
    () =>
      isForgotMode
        ? z.object({
            identifier: z.string().email(t(copy.login.emailInvalid)),
            password: z.string().optional(),
          })
        : z.object({
            identifier: z.string().min(1, t(copy.login.identifierRequired)),
            password: z.string().min(1, t(copy.login.passwordRequired)),
          }),
    [isForgotMode, t]
  );

  useEffect(() => {
    if ((location.state as { message?: unknown })?.message) {
      void navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormInput>({
    resolver: zodResolver(authSchema),
  });

  const onSubmit = async (data: LoginFormInput) => {
    setMessage(null);
    try {
      if (isForgotMode) {
        const response = await getNeonAuthClient().requestPasswordReset({
          email: data.identifier,
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (response.error) throw new Error(response.error.message);
        setMessage({ type: 'success', text: t(copy.login.linkSentSuccess) });
        return;
      }

      if (!data.password) return;
      const neonAuth = getNeonAuthClient();
      const initialSignIn = data.identifier.includes('@')
        ? await neonAuth.signIn.email({
            email: data.identifier.trim().toLowerCase(),
            password: data.password,
          })
        : null;

      if (initialSignIn?.error || !initialSignIn) {
        const legacyIdentity = await apiClient.auth.verifyLegacyCredentials({
          identifier: data.identifier,
          password: data.password,
        });
        const legacySignIn = await neonAuth.signIn.email({
          email: legacyIdentity.email,
          password: data.password,
        });

        if (legacySignIn.error) {
          const enrollment = await neonAuth.signUp.email({
            email: legacyIdentity.email,
            password: data.password,
            name: legacyIdentity.name,
            callbackURL: `${window.location.origin}/verify-email`,
          });
          if (enrollment.error) {
            throw new Error(t(copy.login.authFailed));
          }
        }
      }

      const token = await getNeonJwtToken();
      if (!token) {
        setMessage({ type: 'success', text: t(copy.login.migrationVerificationRequired) });
        return;
      }

      updateApiClientToken(token);
      const user = await apiClient.auth.me();

      setAuth(token, {
        ...user,
        roles: user.roles || ['PARTICIPANT'],
      } as User);
      const destination = getSafeDestination(navigationState);
      void navigate(`${destination.pathname}${destination.search || ''}${destination.hash || ''}`);
    } catch (e: unknown) {
      const error = e as Error;
      setMessage({
        type: 'error',
        text: isRateLimitedError(e)
          ? t(copy.login.tooManyAttempts)
          : error.message || t(copy.login.authFailed),
      });
    }
  };

  return (
    <main className="min-h-screen bg-surface p-3 font-body text-on-surface sm:p-5 lg:p-6">
      <div className="mx-auto grid min-h-[calc(100vh-1.5rem)] max-w-[1440px] overflow-hidden rounded-[2rem] bg-surface-container-low md:min-h-[calc(100vh-2.5rem)] md:grid-cols-[minmax(0,1.12fr)_minmax(25rem,0.88fr)] lg:min-h-[calc(100vh-3rem)]">
        <section className="relative flex min-h-[19rem] flex-col justify-between overflow-hidden bg-primary px-6 py-7 text-on-primary sm:px-10 sm:py-10 md:min-h-0 md:px-12 md:py-12 lg:px-16 lg:py-16">
          <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-secondary/20 blur-3xl" />
          <div className="absolute -bottom-32 left-1/4 h-80 w-80 rounded-full bg-primary-container/60 blur-3xl" />
          <div className="relative flex items-center gap-3">
            <img
              alt="Tarbiyah Sunnah Learning"
              className="h-10 w-auto brightness-0 invert sm:h-12"
              src="/logo.png"
            />
            <span className="h-8 w-px bg-on-primary/25" aria-hidden="true" />
            <BookOpenCheck className="h-5 w-5 text-secondary" aria-hidden="true" />
          </div>
          <div className="relative max-w-2xl py-8 md:py-12">
            <p className="mb-4 font-label text-xs font-bold uppercase tracking-[0.18em] text-secondary">
              <Bilingual {...copy.login.heroStats} />
            </p>
            <h1 className="min-w-0 break-words font-headline text-3xl font-extrabold leading-[1.08] tracking-[-0.035em] sm:text-4xl md:text-5xl lg:text-6xl">
              <Bilingual {...copy.login.heroTitle} />
            </h1>
            <p className="mt-5 max-w-xl text-sm leading-6 text-on-primary-container/90 sm:text-base sm:leading-7 md:text-lg">
              <Bilingual {...copy.login.heroText} />
            </p>
          </div>
          <p className="relative hidden max-w-xl text-sm leading-6 text-on-primary-container/80 md:block">
            <Bilingual {...copy.login.heroStats} />
          </p>
        </section>

        <section className="flex items-center justify-center bg-surface-container-lowest px-5 py-9 sm:px-10 sm:py-12 md:px-12 lg:px-16">
          <div className="w-full max-w-md animate-in fade-in slide-in-from-bottom-4 duration-700">
            <div className="mb-9 flex items-center justify-end">
              <div
                className="flex items-center rounded-lg bg-surface-container p-1 shadow-level1"
                aria-label="Pilihan bahasa"
              >
                <button
                  type="button"
                  onClick={() => setLanguage('id')}
                  aria-label="Bahasa Indonesia"
                  className={`rounded-md px-3 py-1.5 text-[11px] font-bold transition-colors ${language === 'id' ? 'bg-primary text-on-primary shadow-level1' : 'text-on-surface-variant hover:bg-surface-container-high'}`}
                >
                  ID
                </button>
                <button
                  type="button"
                  onClick={() => setLanguage('ar')}
                  aria-label="اللغة العربية"
                  className={`rounded-md px-3 py-1.5 text-[11px] font-bold transition-colors ${language === 'ar' ? 'bg-primary text-on-primary shadow-level1' : 'text-on-surface-variant hover:bg-surface-container-high'}`}
                >
                  AR
                </button>
              </div>
            </div>

            <div className="mb-9">
              <h2 className="font-headline text-3xl font-bold tracking-[-0.03em] text-primary sm:text-4xl">
                {isForgotMode ? t(copy.login.resetTitle) : t(copy.login.title)}
              </h2>
              <p className="mt-3 max-w-md text-sm leading-6 text-on-surface-variant sm:text-base">
                {isForgotMode ? t(copy.login.resetSubtitle) : t(copy.login.subtitle)}
              </p>
            </div>

            {message && (
              <div
                role="status"
                className={`mb-8 rounded-xl p-4 text-sm font-medium shadow-level1 ${message.type === 'success' ? 'bg-success-container/30 text-on-success-container' : 'bg-error-container/30 text-on-error-container'}`}
              >
                {message.text}
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <Input
                label={t(isForgotMode ? copy.login.emailLabel : copy.login.identifierLabel)}
                type={isForgotMode ? 'email' : 'text'}
                autoComplete={isForgotMode ? 'email' : 'username'}
                placeholder={t(
                  isForgotMode ? copy.login.emailPlaceholder : copy.login.identifierPlaceholder
                )}
                {...register('identifier')}
                error={errors.identifier?.message}
              />
              {!isForgotMode && (
                <div>
                  <Input
                    label={t(copy.login.passwordLabel)}
                    type="password"
                    autoComplete="current-password"
                    placeholder={t(copy.login.passwordPlaceholder)}
                    {...register('password')}
                    error={errors.password?.message}
                  />
                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setIsForgotMode(true)}
                      className="bg-transparent font-label text-xs font-bold uppercase tracking-[0.12em] text-primary transition-colors hover:text-secondary active:scale-95"
                    >
                      {t(copy.login.forgotLabel)}
                    </button>
                  </div>
                </div>
              )}
              <div className="space-y-4 pt-2">
                <Button
                  type="submit"
                  variant="secondary"
                  size="lg"
                  isLoading={isSubmitting}
                  className="group flex w-full items-center justify-center gap-3 rounded-lg active:scale-95"
                >
                  {isForgotMode ? t(copy.login.sendLink) : t(copy.login.signInBtn)}
                  {isForgotMode ? (
                    <Send className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                  ) : (
                    <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                  )}
                </Button>
                {isForgotMode && (
                  <button
                    type="button"
                    onClick={() => setIsForgotMode(false)}
                    className="flex w-full items-center justify-center gap-2 bg-transparent text-sm font-bold text-on-surface-variant transition-colors hover:text-primary active:scale-95"
                  >
                    <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                    {t(copy.login.backToLogin)}
                  </button>
                )}
              </div>
            </form>

            <div className="mt-12 rounded-2xl bg-surface-container-low p-5 text-center sm:p-6">
              <p className="mb-4 text-sm text-on-surface-variant">{t(copy.login.notRegistered)}</p>
              <button
                type="button"
                onClick={() => navigate('/register', { state: { from: navigationState?.from } })}
                className="inline-flex w-full items-center justify-center gap-2.5 rounded-lg bg-surface-container-high px-6 py-3 text-sm font-bold text-primary transition-colors hover:bg-surface-container-highest active:scale-95"
              >
                {t(copy.login.createAccount)}
              </button>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
